import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { db } from "../firebase";
import { ref, onValue, off } from "firebase/database";

export default function Reports() {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [whatsappMessage, setWhatsappMessage] = useState("");
  const [sendingStatus, setSendingStatus] = useState("");
  const [overdueBooks, setOverdueBooks] = useState([]);
  const [showOverdueModal, setShowOverdueModal] = useState(false);
  const [stats, setStats] = useState({
    totalTransactions: 0,
    totalIssued: 0,
    totalReturned: 0,
    totalFine: 0,
    overdueCount: 0
  });

  const role = localStorage.getItem("role");
  const userName = localStorage.getItem("userName") || "Admin";

  // Bottom navigation items
  const bottomNavItems = [
    { name: "Dashboard", path: "/admin-dashboard", icon: "🏠", activeIcon: "🏠" },
    { name: "Books", path: "/books", icon: "📚", activeIcon: "📚" },
    { name: "Teachers", path: "/teachers", icon: "👩‍🏫", activeIcon: "👩‍🏫" },
    { name: "Issue/Return", path: "/manage-books", icon: "🔄", activeIcon: "🔄" },
    { name: "Reports", path: "/reports", icon: "📊", activeIcon: "📊" },
    { name: "Settings", path: "/settings", icon: "⚙️", activeIcon: "⚙️" }
  ];

  // Fetch transactions
  useEffect(() => {
    const txnRef = ref(db, "transactions");

    const unsubscribe = onValue(txnRef, (snapshot) => {
      let list = [];
      let issued = 0;
      let returned = 0;
      let totalFine = 0;
      let overdue = 0;
      const today = new Date();
      const overdueList = [];

      snapshot.forEach((child) => {
        const transaction = {
          id: child.key,
          ...child.val()
        };
        list.push(transaction);
        
        if (transaction.status === "issued") issued++;
        if (transaction.status === "returned") returned++;
        if (transaction.fine) totalFine += transaction.fine;
        
        // Check for overdue books
        if (transaction.status === "issued" && transaction.dueDate) {
          const dueDate = new Date(transaction.dueDate);
          if (dueDate < today) {
            overdue++;
            overdueList.push(transaction);
          }
        }
      });

      setData(list.reverse());
      setOverdueBooks(overdueList);
      setStats({
        totalTransactions: list.length,
        totalIssued: issued,
        totalReturned: returned,
        totalFine: totalFine,
        overdueCount: overdue
      });
      setIsLoading(false);
    });

    return () => off(txnRef);
  }, []);

  // Filter logic
  const filteredData = data.filter((item) => {
    if (filter !== "all" && item.status !== filter) return false;
    if (search && !item.teacherId?.toLowerCase().includes(search.toLowerCase()) && 
        !item.teacherName?.toLowerCase().includes(search.toLowerCase()) &&
        !item.bookTitle?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Send WhatsApp Message
  const sendWhatsAppMessage = (transaction) => {
    setSelectedTransaction(transaction);
    const teacherMobile = transaction.teacherMobile;
    const teacherName = transaction.teacherName;
    const bookTitle = transaction.bookTitle;
    const dueDate = transaction.dueDate ? new Date(transaction.dueDate).toLocaleDateString() : "Not set";
    const isOverdue = new Date(transaction.dueDate) < new Date();
    
    let message = "";
    if (transaction.status === "issued") {
      if (isOverdue) {
        message = `⚠️ *OVERDUE BOOK REMINDER* ⚠️\n\nDear *${teacherName}*,\n\nYou have an overdue book:\n📚 *${bookTitle}*\n📅 Due Date: ${dueDate}\n⏰ The book is LATE!\n\nPlease return the book immediately to avoid additional fines.\n\nThank you,\nMIS Library`;
      } else {
        message = `📚 *BOOK ISSUED CONFIRMATION* 📚\n\nDear *${teacherName}*,\n\nYou have issued the following book:\n📖 *${bookTitle}*\n📅 Issue Date: ${new Date(transaction.issueDate).toLocaleDateString()}\n📅 Due Date: ${dueDate}\n\nPlease return the book by the due date to avoid late fees.\n\nThank you,\nMIS Library`;
      }
    } else if (transaction.status === "returned") {
      message = `✅ *BOOK RETURNED SUCCESSFULLY* ✅\n\nDear *${teacherName}*,\n\nYou have returned:\n📖 *${bookTitle}*\n📅 Return Date: ${new Date(transaction.returnDate).toLocaleDateString()}\n💰 Fine Paid: ₹${transaction.fine || 0}\n\nThank you for returning the book on time!\n\nRegards,\nMIS Library`;
    }
    
    setWhatsappMessage(message);
    setShowWhatsAppModal(true);
  };

  // Send Overdue Reminders to All
  const sendOverdueReminders = () => {
    if (overdueBooks.length === 0) {
      alert("No overdue books found!");
      return;
    }
    setShowOverdueModal(true);
  };

  // Send individual overdue reminder
  const sendOverdueReminder = (transaction) => {
    sendWhatsAppMessage(transaction);
  };

  // Open WhatsApp
  const openWhatsApp = () => {
    if (!selectedTransaction) return;
    
    const phoneNumber = selectedTransaction.teacherMobile;
    if (!phoneNumber) {
      alert("❌ Teacher mobile number not available. Please add mobile number to teacher profile.");
      setShowWhatsAppModal(false);
      return;
    }
    
    // Format phone number (remove any non-digit characters)
    const formattedNumber = phoneNumber.replace(/\D/g, '');
    
    // Encode message for URL
    const encodedMessage = encodeURIComponent(whatsappMessage);
    
    // Open WhatsApp
    window.open(`https://wa.me/${formattedNumber}?text=${encodedMessage}`, '_blank');
    
    setSendingStatus("Message sent! WhatsApp will open.");
    setTimeout(() => {
      setShowWhatsAppModal(false);
      setSendingStatus("");
    }, 2000);
  };

  // Send bulk overdue reminders
  const sendBulkOverdueReminders = async () => {
    let sent = 0;
    let failed = 0;
    
    for (const transaction of overdueBooks) {
      if (transaction.teacherMobile) {
        const phoneNumber = transaction.teacherMobile.replace(/\D/g, '');
        const message = `⚠️ *OVERDUE BOOK REMINDER* ⚠️\n\nDear *${transaction.teacherName}*,\n\nYou have an overdue book:\n📚 *${transaction.bookTitle}*\n📅 Due Date: ${new Date(transaction.dueDate).toLocaleDateString()}\n\nPlease return the book immediately.\n\nThank you,\nMIS Library`;
        const encodedMessage = encodeURIComponent(message);
        window.open(`https://wa.me/${phoneNumber}?text=${encodedMessage}`, '_blank');
        sent++;
        // Add small delay between opens
        await new Promise(resolve => setTimeout(resolve, 500));
      } else {
        failed++;
      }
    }
    
    alert(`✅ Sent ${sent} reminders\n❌ Failed: ${failed} (No mobile number)`);
    setShowOverdueModal(false);
  };

  // Export CSV
  const exportCSV = () => {
    let csv = "Teacher ID,Teacher Name,Teacher Mobile,Book Title,Barcode,Issue Date,Return Date,Due Date,Fine,Status\n";
    
    filteredData.forEach((d) => {
      csv += `"${d.teacherId || ""}","${d.teacherName || ""}","${d.teacherMobile || ""}","${d.bookTitle || ""}","${d.barcode || ""}","${formatDate(d.issueDate)}","${formatDate(d.returnDate)}","${formatDate(d.dueDate)}","${d.fine || 0}","${d.status}"\n`;
    });

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `MIS_Library_Report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Export PDF (print)
  const exportPDF = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Library Report - ${new Date().toLocaleDateString()}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h2 { color: #2d3748; }
          .stats { display: flex; gap: 20px; margin-bottom: 20px; flex-wrap: wrap; }
          .stat-card { background: #f7fafc; padding: 15px; border-radius: 8px; flex: 1; min-width: 150px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background: #667eea; color: white; }
          .issued { color: orange; font-weight: bold; }
          .returned { color: green; font-weight: bold; }
          @media print {
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="no-print" style="text-align:center; margin-bottom:20px;">
          <button onclick="window.print()" style="padding:10px 20px;">🖨️ Print</button>
          <button onclick="window.close()" style="padding:10px 20px;">Close</button>
        </div>
        <h2>📊 Library Transaction Report</h2>
        <p>Generated on: ${new Date().toLocaleString()}</p>
        <div class="stats">
          <div class="stat-card"><strong>Total Transactions:</strong> ${stats.totalTransactions}</div>
          <div class="stat-card"><strong>Total Issued:</strong> ${stats.totalIssued}</div>
          <div class="stat-card"><strong>Total Returned:</strong> ${stats.totalReturned}</div>
          <div class="stat-card"><strong>Total Fine:</strong> ₹${stats.totalFine}</div>
          <div class="stat-card"><strong>Overdue Books:</strong> ${stats.overdueCount}</div>
        </div>
        <table>
          <thead>
            <tr><th>Teacher ID</th><th>Teacher Name</th><th>Mobile</th><th>Book Title</th><th>Issue Date</th><th>Due Date</th><th>Return Date</th><th>Fine</th><th>Status</th></tr>
          </thead>
          <tbody>
            ${filteredData.map(d => `
              <tr>
                <td>${d.teacherId || '-'}</td>
                <td>${d.teacherName || '-'}</td>
                <td>${d.teacherMobile || '-'}</td>
                <td>${d.bookTitle || '-'}</td>
                <td>${formatDate(d.issueDate)}</td>
                <td>${formatDate(d.dueDate)}</td>
                <td>${formatDate(d.returnDate)}</td>
                <td>₹${d.fine || 0}</td>
                <td class="${d.status}">${d.status}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Date format
  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-IN");
  };

  // Check if book is overdue
  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      localStorage.clear();
      navigate("/");
    }
  };

  // Access control
  if (role !== "admin") {
    return (
      <div style={styles.accessDenied}>
        <div style={styles.accessDeniedCard}>
          <span style={styles.accessDeniedIcon}>🔒</span>
          <h2>Access Denied</h2>
          <p>You don't have permission to access this page.</p>
          <button onClick={() => navigate("/admin-dashboard")} style={styles.backBtn}>
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>📊 Reports</h1>
          <p style={styles.subtitle}>View and export library transaction reports</p>
        </div>
        <div style={styles.headerRight}>
          <div style={styles.adminProfile} onClick={() => setShowMobileMenu(!showMobileMenu)}>
            <div style={styles.adminAvatar}>
              {userName.charAt(0).toUpperCase()}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {showMobileMenu && (
        <div style={styles.mobileMenu}>
          <div style={styles.mobileMenuItem} onClick={() => { navigate("/profile"); setShowMobileMenu(false); }}>
            <span>👤</span> My Profile
          </div>
          <div style={styles.mobileMenuItem} onClick={() => { navigate("/settings"); setShowMobileMenu(false); }}>
            <span>⚙️</span> Settings
          </div>
          <div style={styles.mobileMenuItem} onClick={() => { handleLogout(); setShowMobileMenu(false); }}>
            <span>🚪</span> Logout
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <span style={styles.statIcon}>📋</span>
          <div>
            <h3 style={styles.statValue}>{stats.totalTransactions}</h3>
            <p style={styles.statLabel}>Total Transactions</p>
          </div>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statIcon}>📖</span>
          <div>
            <h3 style={styles.statValue}>{stats.totalIssued}</h3>
            <p style={styles.statLabel}>Books Issued</p>
          </div>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statIcon}>✅</span>
          <div>
            <h3 style={styles.statValue}>{stats.totalReturned}</h3>
            <p style={styles.statLabel}>Books Returned</p>
          </div>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statIcon}>💰</span>
          <div>
            <h3 style={styles.statValue}>₹{stats.totalFine}</h3>
            <p style={styles.statLabel}>Total Fine</p>
          </div>
        </div>
        <div style={{...styles.statCard, background: stats.overdueCount > 0 ? "rgba(229, 62, 62, 0.1)" : "#fff"}}>
          <span style={styles.statIcon}>⚠️</span>
          <div>
            <h3 style={{...styles.statValue, color: stats.overdueCount > 0 ? "#e53e3e" : "#2d3748"}}>
              {stats.overdueCount}
            </h3>
            <p style={styles.statLabel}>Overdue Books</p>
          </div>
        </div>
      </div>

      {/* Overdue Alert Button */}
      {stats.overdueCount > 0 && (
        <button onClick={sendOverdueReminders} style={styles.overdueAlertBtn}>
          ⚠️ Send Overdue Reminders to {stats.overdueCount} Teacher(s)
        </button>
      )}

      {/* Filters */}
      <div style={styles.filtersCard}>
        <div style={styles.searchWrapper}>
          <span style={styles.searchIcon}>🔍</span>
          <input
            placeholder="Search by Teacher ID, Name, Mobile or Book Title..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.searchInput}
          />
          {search && (
            <button onClick={() => setSearch("")} style={styles.clearBtn}>
              ✕
            </button>
          )}
        </div>

        <div style={styles.filterGroup}>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={styles.select}
          >
            <option value="all">All Transactions</option>
            <option value="issued">📖 Issued Only</option>
            <option value="returned">✅ Returned Only</option>
          </select>

          <div style={styles.exportButtons}>
            <button onClick={exportCSV} style={styles.exportBtn}>
              📥 CSV
            </button>
            <button onClick={exportPDF} style={styles.printBtn}>
              🖨️ Print
            </button>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div style={styles.tableCard}>
        <div style={styles.tableHeader}>
          <h3 style={styles.tableTitle}>📋 Transaction History</h3>
          <span style={styles.tableCount}>{filteredData.length} records</span>
        </div>

        {isLoading ? (
          <div style={styles.loadingState}>
            <div style={styles.spinner}></div>
            <p>Loading reports...</p>
          </div>
        ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeaderRow}>
                  <th style={styles.th}>Teacher</th>
                  <th style={styles.th}>Book</th>
                  <th style={styles.th}>Issue Date</th>
                  <th style={styles.th}>Due Date</th>
                  <th style={styles.th}>Return Date</th>
                  <th style={styles.th}>Fine</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={styles.noData}>
                      <span style={styles.noDataIcon}>📭</span>
                      <p>No transactions found</p>
                    </td>
                  </tr>
                ) : (
                  filteredData.map((d) => {
                    const overdue = isOverdue(d.dueDate) && d.status === "issued";
                    return (
                      <tr key={d.id} style={{...styles.tableRow, ...(overdue ? styles.overdueRow : {})}}>
                        <td style={styles.td}>
                          <div style={styles.teacherInfo}>
                            <div style={styles.teacherAvatar}>
                              {d.teacherName?.charAt(0).toUpperCase() || "T"}
                            </div>
                            <div>
                              <div style={styles.teacherName}>{d.teacherName || d.teacherId || "Unknown"}</div>
                              <div style={styles.teacherId}>ID: {d.teacherId || "N/A"}</div>
                              {d.teacherMobile && <div style={styles.teacherMobile}>📞 {d.teacherMobile}</div>}
                            </div>
                          </div>
                        </td>
                        <td style={styles.td}>
                          <div>
                            <div style={styles.bookTitle}>{d.bookTitle || "Unknown Book"}</div>
                            <div style={styles.bookBarcode}>📖 {d.barcode || "No barcode"}</div>
                          </div>
                        </td>
                        <td style={styles.td}>
                          <span>📅 {formatDate(d.issueDate)}</span>
                        </td>
                        <td style={styles.td}>
                          <div>
                            <span>📅 {formatDate(d.dueDate)}</span>
                            {overdue && (
                              <span style={styles.overdueBadge}>Overdue!</span>
                            )}
                          </div>
                        </td>
                        <td style={styles.td}>
                          {d.returnDate ? formatDate(d.returnDate) : "-"}
                        </td>
                        <td style={styles.td}>
                          {d.fine > 0 ? (
                            <span style={styles.fineAmount}>₹{d.fine}</span>
                          ) : (
                            <span style={styles.noFine}>-</span>
                          )}
                        </td>
                        <td style={styles.td}>
                          <span style={d.status === "issued" ? styles.statusIssued : styles.statusReturned}>
                            {d.status === "issued" ? "📖 Issued" : "✅ Returned"}
                          </span>
                        </td>
                        <td style={styles.td}>
                          {d.status === "issued" && (
                            <button 
                              onClick={() => sendWhatsAppMessage(d)}
                              style={styles.whatsappBtn}
                              title="Send WhatsApp Message"
                            >
                              💬
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* WhatsApp Message Modal */}
      {showWhatsAppModal && selectedTransaction && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h3>💬 Send WhatsApp Message</h3>
              <button onClick={() => setShowWhatsAppModal(false)} style={styles.closeModalBtn}>✕</button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.recipientInfo}>
                <strong>To:</strong> {selectedTransaction.teacherName}
                <br />
                <strong>Mobile:</strong> {selectedTransaction.teacherMobile || "Not available"}
              </div>
              <label style={styles.messageLabel}>Message Preview:</label>
              <textarea
                value={whatsappMessage}
                onChange={(e) => setWhatsappMessage(e.target.value)}
                style={styles.messageTextarea}
                rows="10"
              />
              {sendingStatus && <p style={styles.sendingStatus}>{sendingStatus}</p>}
            </div>
            <div style={styles.modalFooter}>
              <button onClick={openWhatsApp} style={styles.sendBtn}>
                📤 Send via WhatsApp
              </button>
              <button onClick={() => setShowWhatsAppModal(false)} style={styles.cancelBtn}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Overdue Reminders Modal */}
      {showOverdueModal && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h3>⚠️ Send Overdue Reminders</h3>
              <button onClick={() => setShowOverdueModal(false)} style={styles.closeModalBtn}>✕</button>
            </div>
            <div style={styles.modalBody}>
              <p>Found <strong>{overdueBooks.length}</strong> overdue book(s).</p>
              <div style={styles.overdueList}>
                {overdueBooks.map((book, idx) => (
                  <div key={idx} style={styles.overdueListItem}>
                    <span>📚 {book.bookTitle}</span>
                    <span>👤 {book.teacherName}</span>
                    <span>📞 {book.teacherMobile || "No mobile"}</span>
                    {book.teacherMobile && (
                      <button onClick={() => sendOverdueReminder(book)} style={styles.reminderBtn}>
                        Send
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button onClick={sendBulkOverdueReminders} style={styles.sendBtn}>
                📤 Send All Reminders
              </button>
              <button onClick={() => setShowOverdueModal(false)} style={styles.cancelBtn}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation Bar */}
      <div style={styles.bottomNav}>
        {bottomNavItems.map((item) => (
          <div
            key={item.path}
            style={{
              ...styles.bottomNavItem,
              ...(location.pathname === item.path && styles.bottomNavItemActive)
            }}
            onClick={() => handleNavigation(item.path)}
          >
            <div style={styles.bottomNavIcon}>
              {location.pathname === item.path ? item.activeIcon : item.icon}
            </div>
            <span style={styles.bottomNavLabel}>{item.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "clamp(16px, 4vw, 32px)",
    paddingBottom: "80px",
    fontFamily: "'Segoe UI', 'Inter', system-ui, sans-serif",
    backgroundColor: "#f7fafc",
    minHeight: "100vh",
    overflowY: "auto",  // Add this
    WebkitOverflowScrolling: "touch",  // Add this
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: "16px",
    marginBottom: "24px",
  },

  title: {
    fontSize: "clamp(24px, 5vw, 32px)",
    fontWeight: "700",
    margin: "0 0 8px 0",
    color: "#2d3748",
  },

  subtitle: {
    fontSize: "14px",
    color: "#718096",
    margin: 0,
  },

  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },

  adminProfile: {
    cursor: "pointer",
  },

  adminAvatar: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #667eea, #764ba2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontWeight: "bold",
    fontSize: "18px",
  },

  mobileMenu: {
    position: "absolute",
    top: "70px",
    right: "20px",
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    zIndex: 1000,
    minWidth: "160px",
    overflow: "hidden",
  },

  mobileMenuItem: {
    padding: "12px 16px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    fontSize: "14px",
    color: "#2d3748",
    cursor: "pointer",
    transition: "background 0.2s ease",
    borderBottom: "1px solid #e2e8f0",
  },

  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: "16px",
    marginBottom: "24px",
  },

  statCard: {
    backgroundColor: "#fff",
    padding: "16px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    border: "1px solid #e2e8f0",
  },

  statIcon: {
    fontSize: "32px",
  },

  statValue: {
    fontSize: "24px",
    fontWeight: "700",
    margin: 0,
    color: "#2d3748",
  },

  statLabel: {
    fontSize: "12px",
    color: "#718096",
    margin: 0,
  },

  overdueAlertBtn: {
    width: "100%",
    padding: "14px",
    backgroundColor: "#e53e3e",
    color: "#fff",
    border: "none",
    borderRadius: "12px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    marginBottom: "20px",
    transition: "all 0.3s ease",
  },

  filtersCard: {
    backgroundColor: "#fff",
    borderRadius: "16px",
    padding: "20px",
    marginBottom: "20px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    border: "1px solid #e2e8f0",
  },

  searchWrapper: {
    position: "relative",
    marginBottom: "16px",
  },

  searchIcon: {
    position: "absolute",
    left: "14px",
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: "16px",
    color: "#a0aec0",
  },

  searchInput: {
    width: "100%",
    padding: "12px 40px 12px 40px",
    borderRadius: "12px",
    border: "2px solid #e2e8f0",
    fontSize: "14px",
    transition: "all 0.3s ease",
    boxSizing: "border-box",
  },

  clearBtn: {
    position: "absolute",
    right: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "#e2e8f0",
    border: "none",
    borderRadius: "50%",
    width: "22px",
    height: "22px",
    cursor: "pointer",
    fontSize: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  filterGroup: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
  },

  select: {
    flex: 2,
    padding: "10px 12px",
    borderRadius: "10px",
    border: "2px solid #e2e8f0",
    fontSize: "14px",
    backgroundColor: "#fff",
    cursor: "pointer",
  },

  exportButtons: {
    display: "flex",
    gap: "8px",
    flex: 1,
  },

  exportBtn: {
    flex: 1,
    padding: "10px 12px",
    backgroundColor: "#48bb78",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
  },

  printBtn: {
    flex: 1,
    padding: "10px 12px",
    backgroundColor: "#667eea",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
  },

  tableCard: {
    backgroundColor: "#fff",
    borderRadius: "16px",
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    border: "1px solid #e2e8f0",
  },

  tableHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 20px",
    borderBottom: "1px solid #e2e8f0",
    backgroundColor: "#fafafa",
  },

  tableTitle: {
    fontSize: "16px",
    fontWeight: "600",
    margin: 0,
    color: "#2d3748",
  },

  tableCount: {
    fontSize: "13px",
    color: "#718096",
    backgroundColor: "#edf2f7",
    padding: "4px 10px",
    borderRadius: "20px",
  },

  loadingState: {
    textAlign: "center",
    padding: "60px 20px",
    color: "#718096",
  },

  spinner: {
    width: "40px",
    height: "40px",
    border: "3px solid #e2e8f0",
    borderTop: "3px solid #667eea",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
    margin: "0 auto 16px",
  },

  tableWrapper: {
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
  },

  tableHeaderRow: {
    backgroundColor: "#f7fafc",
  },

  th: {
    textAlign: "left",
    padding: "14px 12px",
    fontSize: "13px",
    fontWeight: "600",
    color: "#4a5568",
    borderBottom: "1px solid #e2e8f0",
  },

  tableRow: {
    borderBottom: "1px solid #e2e8f0",
    transition: "background-color 0.2s ease",
  },

  overdueRow: {
    backgroundColor: "rgba(229, 62, 62, 0.05)",
  },

  td: {
    padding: "14px 12px",
    fontSize: "14px",
    color: "#2d3748",
    verticalAlign: "top",
  },

  teacherInfo: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },

  teacherAvatar: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    backgroundColor: "#667eea",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    fontWeight: "600",
  },

  teacherName: {
    fontWeight: "600",
    fontSize: "14px",
  },

  teacherId: {
    fontSize: "11px",
    color: "#718096",
  },

  teacherMobile: {
    fontSize: "11px",
    color: "#48bb78",
  },

  bookTitle: {
    fontWeight: "500",
    fontSize: "14px",
  },

  bookBarcode: {
    fontSize: "11px",
    color: "#718096",
    marginTop: "2px",
  },

  overdueBadge: {
    display: "inline-block",
    marginLeft: "8px",
    padding: "2px 6px",
    backgroundColor: "#e53e3e",
    color: "#fff",
    borderRadius: "4px",
    fontSize: "10px",
    fontWeight: "600",
  },

  fineAmount: {
    color: "#e53e3e",
    fontWeight: "600",
  },

  noFine: {
    color: "#a0aec0",
  },

  statusIssued: {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "600",
    backgroundColor: "#fefcbf",
    color: "#744210",
  },

  statusReturned: {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "600",
    backgroundColor: "#c6f6d5",
    color: "#22543d",
  },

  whatsappBtn: {
    padding: "8px 12px",
    backgroundColor: "#25D366",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "16px",
    transition: "all 0.3s ease",
  },

  noData: {
    textAlign: "center",
    padding: "60px 20px",
    color: "#a0aec0",
  },

  noDataIcon: {
    fontSize: "48px",
    display: "block",
    marginBottom: "16px",
  },

  modal: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.8)",
    zIndex: 2000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  modalContent: {
    backgroundColor: "#fff",
    borderRadius: "16px",
    width: "90%",
    maxWidth: "500px",
    maxHeight: "80vh",
    overflow: "auto",
  },

  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 20px",
    borderBottom: "1px solid #e2e8f0",
  },

  closeModalBtn: {
    background: "none",
    border: "none",
    fontSize: "24px",
    cursor: "pointer",
    padding: "0 8px",
  },

  modalBody: {
    padding: "20px",
  },

  recipientInfo: {
    marginBottom: "16px",
    padding: "12px",
    backgroundColor: "#f7fafc",
    borderRadius: "8px",
    fontSize: "14px",
  },

  messageLabel: {
    display: "block",
    marginBottom: "8px",
    fontWeight: "600",
    color: "#2d3748",
  },

  messageTextarea: {
    width: "100%",
    padding: "12px",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    fontSize: "14px",
    fontFamily: "monospace",
    resize: "vertical",
  },

  sendingStatus: {
    marginTop: "12px",
    padding: "8px",
    backgroundColor: "#c6f6d5",
    color: "#22543d",
    borderRadius: "8px",
    fontSize: "12px",
  },

  modalFooter: {
    display: "flex",
    gap: "12px",
    padding: "16px 20px",
    borderTop: "1px solid #e2e8f0",
  },

  sendBtn: {
    flex: 1,
    padding: "12px",
    backgroundColor: "#25D366",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },

  cancelBtn: {
    flex: 1,
    padding: "12px",
    backgroundColor: "#e2e8f0",
    color: "#4a5568",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },

  overdueList: {
    maxHeight: "300px",
    overflowY: "auto",
    marginTop: "16px",
  },

  overdueListItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px",
    borderBottom: "1px solid #e2e8f0",
    flexWrap: "wrap",
    gap: "8px",
  },

  reminderBtn: {
    padding: "6px 12px",
    backgroundColor: "#25D366",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "12px",
  },

  bottomNav: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "#fff",
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    padding: "8px 16px",
    boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
    zIndex: 100,
    borderTop: "1px solid #e2e8f0",
  },

  bottomNavItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "4px",
    cursor: "pointer",
    padding: "6px 12px",
    borderRadius: "8px",
    transition: "all 0.2s ease",
    flex: 1,
  },

  bottomNavItemActive: {
    color: "#667eea",
  },

  bottomNavIcon: {
    fontSize: "22px",
  },

  bottomNavLabel: {
    fontSize: "11px",
    fontWeight: "500",
  },

  accessDenied: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  },

  accessDeniedCard: {
    textAlign: "center",
    padding: "40px",
    background: "#fff",
    borderRadius: "16px",
    maxWidth: "400px",
    margin: "20px",
  },

  accessDeniedIcon: {
    fontSize: "64px",
    display: "block",
    marginBottom: "16px",
  },

  backBtn: {
    marginTop: "20px",
    padding: "12px 24px",
    background: "#667eea",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    cursor: "pointer",
  },
};

// Add animations
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  input:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  button:hover:not(:disabled) {
    transform: translateY(-2px);
    filter: brightness(1.05);
  }
  
  .table-row:hover {
    background-color: #f7fafc;
  }
  
  @media (max-width: 768px) {
    .stats-grid {
      grid-template-columns: repeat(2, 1fr);
    }
    
    .filter-group {
      flex-direction: column;
    }
    
    .export-buttons {
      width: 100%;
    }
    
    th, td {
      padding: 8px;
      font-size: 12px;
    }
    
    .teacher-info {
      flex-direction: column;
      text-align: center;
    }
    
    .whatsapp-btn {
      padding: 6px 10px;
    }
  }
`;
document.head.appendChild(styleSheet);